import React, { useState, useRef, useEffect } from 'react';

const SimpleTable = ({ columns = [] }) => {
	const [data, setData] = useState([
		{
			id: 1,
			name: 'Alice Johnson',
			email: 'alice@example.com',
			role: 'Engineer',
			status: 'Active',
			joinDate: '2024-01-15',
		},
		{
			id: 2,
			name: 'Bob Smith',
			email: 'bob@example.com',
			role: 'Designer',
			status: 'Active',
			joinDate: '2024-02-20',
		},
		{
			id: 3,
			name: 'Carol White',
			email: 'carol@example.com',
			role: 'Manager',
			status: 'Inactive',
			joinDate: '2023-11-10',
		},
		{
			id: 4,
			name: 'David Brown',
			email: 'david@example.com',
			role: 'Developer',
			status: 'Active',
			joinDate: '2024-03-05',
		},
		{
			id: 5,
			name: 'Eve Davis',
			email: 'eve@example.com',
			role: 'Analyst',
			status: 'Active',
			joinDate: '2024-01-20',
		},
	]);

	const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
	const [openMenu, setOpenMenu] = useState(null);
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [filterOpen, setFilterOpen] = useState(false);
	const filterRef = useRef(null);

	// Extract unique roles
	const uniqueRoles = [...new Set(data.map((row) => row.role))].sort();

	// Close filter when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (filterRef.current && !filterRef.current.contains(event.target)) {
				setFilterOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSort = (key) => {
		let direction = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	const toggleRole = (role) => {
		setSelectedRoles((prev) =>
			prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
		);
	};

	const clearRoleFilter = () => {
		setSelectedRoles([]);
	};

	const sortedData = [...data]
		.sort((a, b) => {
			if (!sortConfig.key) return 0;

			const aValue = a[sortConfig.key];
			const bValue = b[sortConfig.key];

			if (typeof aValue === 'string') {
				return sortConfig.direction === 'asc'
					? aValue.localeCompare(bValue)
					: bValue.localeCompare(aValue);
			}

			return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
		})
		.filter((row) => {
			if (selectedRoles.length === 0) return true;
			return selectedRoles.includes(row.role);
		});

	const handleAction = (action, rowId) => {
		console.log(`Action: ${action}, Row ID: ${rowId}`);
		setOpenMenu(null);
	};

	const handleDelete = (rowId) => {
		setData(data.filter((row) => row.id !== rowId));
		setOpenMenu(null);
	};

	return (
		<div className="w-full py-4 md:py-6">
			{/* Filter Section */}
			<div className="mb-4 relative" ref={filterRef}>
				<div className="flex items-center gap-2">
					<button
						onClick={() => setFilterOpen(!filterOpen)}
						className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
					>
						{/*} <Filter size={18} className="text-gray-600" />*/}
						<span className="text-sm font-medium text-gray-700">
							Document Type
						</span>
						{selectedRoles.length > 0 && (
							<span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
								{selectedRoles.length}
							</span>
						)}
						{/*<ChevronDown
              size={16}
              className={`text-gray-600 transition-transform ${filterOpen ? 'rotate-180' : ''}`}
            />*/}
					</button>
					{selectedRoles.length > 0 && (
						<button
							onClick={clearRoleFilter}
							className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-600"
							title="Clear filters"
						>
							{/*<X size={18} />*/}
						</button>
					)}
				</div>

				{/* Filter Dropdown */}
				{filterOpen && (
					<div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-64 p-3">
						<div className="max-h-64 overflow-y-auto">
							{uniqueRoles.length > 0 ? (
								uniqueRoles.map((role) => (
									<label
										key={role}
										className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors"
									>
										<input
											type="checkbox"
											checked={selectedRoles.includes(role)}
											onChange={() => toggleRole(role)}
											className="w-4 h-4 text-blue-600 rounded cursor-pointer"
										/>
										<span className="text-sm text-gray-700">{role}</span>
									</label>
								))
							) : (
								<p className="text-sm text-gray-500 p-2">No roles available</p>
							)}
						</div>
						{uniqueRoles.length > 0 && (
							<div className="border-t border-gray-200 mt-2 pt-2">
								<button
									onClick={clearRoleFilter}
									className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
								>
									Clear All
								</button>
							</div>
						)}
					</div>
				)}
			</div>
			{/* Desktop Table View */}
			<div className="hidden md:block bg-white shadow overflow-hidden">
				<div className="overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr className="bg-gray-100 border-b border-gray-200">
								{columns.map((column) => (
									<th
										key={column.key}
										onClick={() => handleSort(column.key)}
										className="px-4 py-3 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
									>
										<div className="flex items-center gap-2">
											{column.header}
											{/*}
                        size={16}
                        className={`transition-transform ${
                          sortConfig.key === column.key
                            ? sortConfig.direction === 'asc'
                              ? 'rotate-0'
                              : 'rotate-180'
                            : 'opacity-40'
                        }`}
                      />*/}
										</div>
									</th>
								))}
								<th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 whitespace-nowrap"></th>
							</tr>
						</thead>
						<tbody>
							{sortedData.map((row, index) => (
								<tr
									key={row.id}
									className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
										index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
									}`}
								>
									{columns.map((column) => (
										<td
											key={`${row.id}-${column.key}`}
											className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap"
										>
											{row[column.key]}
										</td>
									))}
									<td className="px-4 py-3 text-center">
										<div className="relative inline-block">
											<button
												onClick={() =>
													setOpenMenu(openMenu === row.id ? null : row.id)
												}
												className="p-2 hover:bg-gray-200 rounded-md transition-colors"
												aria-label="Open menu"
											>
												{/*<MoreVertical size={18} className="text-gray-600" />*/}
											</button>
											{openMenu === row.id && (
												<div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
													<button
														onClick={() => handleAction('edit', row.id)}
														className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 transition-colors"
													>
														Edit
													</button>
													<button
														onClick={() => handleAction('view', row.id)}
														className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 transition-colors"
													>
														View Details
													</button>
													<button
														onClick={() => handleDelete(row.id)}
														className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
													>
														Delete
													</button>
												</div>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{sortedData.length === 0 && (
					<div className="text-center py-8 text-gray-500">
						No data available
					</div>
				)}
			</div>

			{/* Mobile Stacked List View */}
			<div className="md:hidden space-y-3">
				<div className="flex items-center gap-2 mb-4">
					<h2 className="text-lg font-semibold text-gray-800">Records</h2>
					<button
						onClick={() => handleSort('name')}
						className="p-2 hover:bg-gray-200 rounded-md transition-colors"
						title="Sort by name"
					>
						{/*<ChevronDown
              size={18}
              className={`transition-transform text-gray-600 ${
                sortConfig.key === 'name'
                  ? sortConfig.direction === 'asc'
                    ? 'rotate-0'
                    : 'rotate-180'
                  : 'opacity-40'
              }`}
            />*/}
					</button>
				</div>
				{sortedData.map((row) => (
					<div
						key={row.id}
						className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500"
					>
						<div className="flex justify-between items-start mb-3">
							<h3 className="font-semibold text-gray-800">{row.name}</h3>
							<div className="relative">
								<button
									onClick={() =>
										setOpenMenu(openMenu === row.id ? null : row.id)
									}
									className="p-1 hover:bg-gray-200 rounded-md transition-colors"
									aria-label="Open menu"
								>
									{/*<MoreVertical size={18} className="text-gray-600" />*/}
								</button>
								{openMenu === row.id && (
									<div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
										<button
											onClick={() => handleAction('edit', row.id)}
											className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 transition-colors"
										>
											Edit
										</button>
										<button
											onClick={() => handleAction('view', row.id)}
											className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 transition-colors"
										>
											View Details
										</button>
										<button
											onClick={() => handleDelete(row.id)}
											className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
										>
											Delete
										</button>
									</div>
								)}
							</div>
						</div>
						<div className="space-y-2 text-sm">
							{columns.map(
								(column) =>
									column.key !== 'name' && (
										<div key={column.key} className="flex justify-between">
											<span className="text-gray-600 font-medium">
												{column.label}:
											</span>
											<span className="text-gray-800">{row[column.key]}</span>
										</div>
									),
							)}
						</div>
					</div>
				))}
				{sortedData.length === 0 && (
					<div className="text-center py-8 text-gray-500">
						No data available
					</div>
				)}
			</div>
		</div>
	);
};

export default SimpleTable;
